import { FC, ReactNode } from 'react';
import { Link, LinkProps, NavLink, NavLinkProps } from 'react-router-dom';
import { useLocalizedPath } from '@app/app/i18n/hooks/use-localized-path';

export interface LocalizedLinkProps extends Omit<LinkProps, 'to'> {
  to: string;
  /**
   * Если true, не добавляет языковой префикс к ссылке
   * Используется для внешних ссылок, sharing links и т.д.
   */
  skipLocalization?: boolean;
  children?: ReactNode;
}

/**
 * Компонент-обертка над Link из react-router-dom, который автоматически
 * добавляет языковой префикс к внутренним ссылкам.
 *
 * @example
 * // Для английского языка (en) - без префикса
 * <LocalizedLink to="/features/">Features</LocalizedLink>
 * // → /features/
 *
 * // Для испанского языка (es) - с префиксом
 * <LocalizedLink to="/features/">Features</LocalizedLink>
 * // → /es/features/
 *
 * // Для внешних ссылок или sharing links
 * <LocalizedLink to="/s/abc123" skipLocalization>Share</LocalizedLink>
 * // → /s/abc123 (без префикса)
 */
export const LocalizedLink: FC<LocalizedLinkProps> = ({
  to,
  skipLocalization = false,
  children,
  ...props
}) => {
  const localizedPath = useLocalizedPath(to, skipLocalization);

  return (
    <Link to={localizedPath} {...props}>
      {children}
    </Link>
  );
};

export interface LocalizedNavLinkProps extends Omit<NavLinkProps, 'to'> {
  to: string;
  /**
   * Если true, не добавляет языковой префикс к ссылке
   * Используется для внешних ссылок, sharing links и т.д.
   */
  skipLocalization?: boolean;
  children?: ReactNode;
}

/**
 * Компонент-обертка над NavLink из react-router-dom, который автоматически
 * добавляет языковой префикс к внутренним ссылкам и поддерживает активное состояние.
 *
 * @example
 * <LocalizedNavLink to="/features/" className={({ isActive }) => ...}>
 *   Features
 * </LocalizedNavLink>
 */
export const LocalizedNavLink: FC<LocalizedNavLinkProps> = ({
  to,
  skipLocalization = false,
  children,
  ...props
}) => {
  const localizedPath = useLocalizedPath(to, skipLocalization);

  return (
    <NavLink to={localizedPath} {...props}>
      {children}
    </NavLink>
  );
};
